// The reseller's own shapes, shared by the API and the SPA (README → Monorepo): sign-up and
// the signed-in reseller's profile. A field renamed here fails both typechecks.
import countries from "i18n-iso-countries";
import { z } from "zod";

/** Every status reseller-machine.ts (apps/api) can hold; the database CHECK reads the same list. */
export const RESELLER_STATUSES = ["pending", "active", "suspended", "rejected"] as const;
export const ResellerStatus = z.enum(RESELLER_STATUSES);
export type ResellerStatus = z.infer<typeof ResellerStatus>;

/** What staff narrow the reseller list to: one status, or every reseller. */
export const ResellerStatusFilter = z.enum([...RESELLER_STATUSES, "all"]);
export type ResellerStatusFilter = z.infer<typeof ResellerStatusFilter>;

/** A key on the wire: 22 Base62 characters, never a raw UUID (screaming.md rule 19). */
const Base62Id = z.string().regex(/^[0-9A-Za-z]{22}$/);

// No resellerId (rule 10): the reseller does not exist until this request makes it.
export const SignUpRequest = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.email().max(254),
  // The API's Better Auth minPasswordLength is the same 12.
  password: z.string().min(12).max(128),
  company: z.string().trim().min(1).max(200),
});
export type SignUpRequest = z.infer<typeof SignUpRequest>;

// The same answer for a new address and a known one: it never says which has an account.
export const SignUpResponse = z.object({ data: z.object({ status: z.literal("pending") }) });

export const ResellerProfile = z.object({ id: Base62Id, name: z.string(), status: ResellerStatus });
export type ResellerProfile = z.infer<typeof ResellerProfile>;
export const GetProfileResponse = z.object({ data: ResellerProfile });

const required = (max: number) => z.string().trim().min(1).max(max);

/** Where the reseller's company sits; the country is its ISO 3166-1 alpha-2 code, never its name. */
export const CompanyAddress = z.object({
  addressLine: required(200),
  city: required(100),
  province: required(100),
  postcode: z
    .string()
    .trim()
    .regex(/^[0-9A-Za-z -]{3,10}$/),
  // The database CHECK holds the pattern; the library knows which codes are a country.
  country: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .refine((code) => countries.isValid(code)),
});

// What a pending reseller tells staff so the application can be reviewed. No resellerId (rule 10).
export const ResellerInformation = z.object({
  contactName: required(100),
  phone: z
    .string()
    .trim()
    .regex(/^\+?[0-9][0-9 ()-]{5,19}$/),
  // KTP, SIM, passport: the formats differ too much to check more than presence.
  idNumber: required(50),
  // Written with or without the dots and dashes; 15 digits, or 16 since the NIK-based NPWP.
  npwp: z
    .string()
    .trim()
    .refine((value) => /^[0-9]{15,16}$/.test(value.replace(/[.\-\s]/g, ""))),
  address: CompanyAddress,
  website: z
    .string()
    .trim()
    .transform((value) => value || undefined)
    .pipe(
      z
        .url({ protocol: /^https?$/ })
        .max(200)
        .optional(),
    ),
  // NIB or SIUP number.
  businessNumber: required(50),
});
export type ResellerInformation = z.infer<typeof ResellerInformation>;

export const SubmitInformationResponse = z.object({ data: z.object({ status: z.literal("pending") }) });

// What staff read back. Not ResellerInformation: its website has a .transform, so it validates
// input but does not describe a response.
export const ResellerInformationView = z.object({
  contactName: z.string(),
  phone: z.string(),
  idNumber: z.string(),
  npwp: z.string(),
  address: CompanyAddress,
  website: z.string().nullable(),
  businessNumber: z.string(),
});

export type ResellerInformationView = z.infer<typeof ResellerInformationView>;

export const PendingReseller = z.object({
  id: Base62Id,
  name: z.string(),
  status: ResellerStatus,
  createdAt: z.iso.datetime(),
  information: ResellerInformationView.nullable(),
});
export type PendingReseller = z.infer<typeof PendingReseller>;

export const ListResellersResponse = z.object({ data: z.array(PendingReseller) });

/** Staff's answer to an application, once made: a rejection carries the reason the applicant read. */
export const ResellerDecision = z.object({
  outcome: z.enum(["approved", "rejected"]),
  reason: z.string().nullable(),
  decidedAt: z.iso.datetime(),
});
export type ResellerDecision = z.infer<typeof ResellerDecision>;

/** Why staff suspended an active reseller, and whether they overrode what it still had in flight. */
export const ResellerSuspension = z.object({
  reason: z.string(),
  forced: z.boolean(),
  suspendedAt: z.iso.datetime(),
});
export type ResellerSuspension = z.infer<typeof ResellerSuspension>;

// One reseller as staff read it: the list's row, the decision on it (null while it waits) and
// the suspension in force (null unless it is suspended).
export const ResellerDetail = PendingReseller.extend({
  decision: ResellerDecision.nullable(),
  suspension: ResellerSuspension.nullable(),
});
export type ResellerDetail = z.infer<typeof ResellerDetail>;

export const GetResellerResponse = z.object({ data: ResellerDetail });

// What the signed-in reseller sent before, to fill the form again; null until it sends.
export const GetInformationResponse = z.object({ data: ResellerInformationView.nullable() });

// Staff turn an application down with a reason; the applicant reads it in the rejection email.
export const RejectResellerRequest = z.object({ reason: z.string().trim().min(1).max(1000) });
export type RejectResellerRequest = z.infer<typeof RejectResellerRequest>;

// Staff suspend an active reseller with a reason. What it still has in flight (an order the
// registry has not answered, a top-up paid but not credited) refuses the suspension; force is
// the compliance takedown that suspends anyway (screaming.md rule 18).
export const SuspendResellerRequest = z.object({
  reason: z.string().trim().min(1).max(1000),
  force: z.boolean().default(false),
});
export type SuspendResellerRequest = z.input<typeof SuspendResellerRequest>;

/** What approve, reject, suspend and reinstate answer: the reseller with its new status. */
export const ResellerDecisionResponse = z.object({ data: ResellerProfile });
